import { useCallback, useEffect, useRef, useState } from 'react'
import Drawer from './Drawer'
import Icon from './Icon'
import DateRangePicker from './DateRangePicker'
import { dateText } from '../lib/when'
import { monthRange } from '../lib/period'
import { fetchClientLedgers, fetchAllClientLedgers, fetchLedgerFileBytes } from '../lib/clientLedgers'
import { buildZip, saveBlob } from '../lib/zip'
import { SkeletonBlock } from './Skeleton'
import Modal from './Modal'
import * as api from '../lib/api'

/**
 * A client's collected ledgers, as dated folders — one per time we asked.
 *
 * Opened by clicking the row (the WhatsApp chip opens the chat instead). Each
 * folder is dated when the statement came in and holds the two files that get
 * reconciled: the client's ledger and ours.
 *
 * Data comes from `fetchClientLedgers`, which is paginated and filter/sort
 * aware. This component never generates data; it only asks for a page and
 * renders it — which is exactly what let the real endpoint land underneath it
 * with nothing here changing.
 */

const PAGE_SIZE = 5

/**
 * Hand in their statement ourselves — for one that reached us outside
 * WhatsApp and email. Confirmed, because it's filed against exactly one
 * request: the chase stops and the cycle moves on to reconciliation. If the
 * cycle was waiting on a corrected statement, this is that statement.
 */
function UploadModal({ request, onClose, onDone }) {
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const received = Boolean(request.theirSide?.receivedAt)

  const submit = async () => {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const out = await api.uploadStatement(request._id, file)
      onDone(out)
    } catch (e) {
      setError(e.message)
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`Upload ${request.clientName || request.clientCode}'s statement`}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !file}>
            {busy ? 'Uploading…' : 'Upload and mark received'}
          </button>
        </>
      }
    >
      <div className="rm-confirm">
        <p className="rm-confirm-lead">
          For a statement that reached us some other way — handed over, a personal inbox, a call. It's filed as{' '}
          <b>received and confirmed</b> for {request.periodLabel || 'the current cycle'}: the chase stops, our side is pulled
          from Gati for the same dates, and it goes to reconciliation.
          {received && ' This replaces the statement already on file — use it for a corrected resend.'}
        </p>
        <label className="field" style={{ marginBottom: 0 }}>
          <span className="rm-lbl">Their ledger statement (PDF, Excel, CSV or a photo)</span>
          <input
            type="file"
            accept=".pdf,.xlsx,.xls,.csv,image/png,image/jpeg,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {file && (
          <div className="c-sub" style={{ marginTop: 0 }}>
            {file.name} · {Math.max(1, Math.round(file.size / 1024))} KB
          </div>
        )}
        {error && <div className="rm-error">{error}</div>}
      </div>
    </Modal>
  )
}

export default function ClientLedgers({ request, onClose, onChanged }) {
  const [uploading, setUploading] = useState(false)
  const [notice, setNotice] = useState(null)
  // A client never asked has no request to file against — the Collector's
  // placeholder rows carry a `ledger-` id, not a real one.
  const canUpload = Boolean(request?._id) && !String(request._id).startsWith('ledger-')
  // Applied range — what the query actually filters on.
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  // Draft range — what the calendar is currently showing. Kept apart so picking
  // dates does not refetch on every click; Apply is what commits it.
  const [draft, setDraft] = useState({ from: '', to: '' })
  const [closeSignal, setCloseSignal] = useState(0)
  const [sortBy, setSortBy] = useState('newest')

  const [zipping, setZipping] = useState(null)   // 'all' | folder id | null

  /*
   * Refs beside the state the observer needs.
   *
   * The IntersectionObserver callback is created once per `hasMore` change and
   * would otherwise close over whatever `page`/`loading` were at that moment —
   * so it would keep asking for the same page. Refs are read live.
   */
  const sentinelRef = useRef(null)
  const loadingRef = useRef(false)
  const pageRef = useRef(1)

  const dirty = draft.from !== from || draft.to !== to
  const canApply = dirty && (!draft.from || draft.to)   // a half-picked range is not applyable
  const canClear = Boolean(from || to || draft.from || draft.to)

  const [folders, setFolders] = useState([])
  // The current ask, if one is still open — asked for, nothing received yet.
  // Never paginated, never filtered by date: it isn't one of the documented
  // cycles below, it's the one still in progress. Only ever set on page 1.
  const [openFolder, setOpenFolder] = useState(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState(null)

  /*
   * Page 1 reloads whenever a filter or the sort changes; "Load more" appends
   * the next page. `append` is what tells the two apart — a filter change
   * replaces the list, paging grows it.
   */
  const load = useCallback(
    async (nextPage, append) => {
      loadingRef.current = true
      setLoading(true)
      try {
        const res = await fetchClientLedgers({ request, from, to, sortBy, page: nextPage, limit: PAGE_SIZE })
        setFolders((prev) => (append ? [...prev, ...res.folders] : res.folders))
        setTotal(res.total)
        setHasMore(res.hasMore)
        setPage(res.page)
        pageRef.current = res.page
        if (!append) {
          setOpenId(res.folders[0]?.id ?? null)
          setOpenFolder(res.openFolder ?? null)
        }
      } finally {
        loadingRef.current = false
        setLoading(false)
      }
    },
    [request, from, to, sortBy]
  )

  useEffect(() => {
    load(1, false)
  }, [load])

  /*
   * Load the next page when the bottom comes into view.
   *
   * An IntersectionObserver on a sentinel below the list, not a scroll handler:
   * scroll fires continuously and would need throttling and its own maths about
   * where the bottom is, while this fires once when the element is actually
   * visible — and works whichever element is doing the scrolling, which matters
   * here because the list scrolls inside the drawer body rather than the page.
   *
   * `loadingRef` guards against a second fetch while one is in flight: the
   * sentinel can intersect again before React has re-rendered with the new
   * page, and without it a fast scroll requests the same page twice.
   */
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasMore) return

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current) load(pageRef.current + 1, true)
      },
      // A little early, so the next page is usually there by the time the
      // reader arrives rather than after a visible stall.
      { root: el.closest('.drw-b') ?? null, rootMargin: '160px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, load])

  /**
   * The client's statement for one cycle, saved as-is — each folder holds
   * just their ledger now, so there is nothing to zip. Named by client and
   * the day it arrived, so a downloads folder of them still sorts sensibly.
   */
  const downloadFolder = async (folder) => {
    const file = folder.files[0]
    if (!file) return
    setZipping(folder.id)
    try {
      const bytes = await fetchLedgerFileBytes({ file })
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : ''
      saveBlob(new Blob([bytes]), `${request.clientCode}-${folder.receivedOn}-statement${ext}`)
    } finally {
      setZipping(null)
    }
  }

  /**
   * Every ledger in the current filter, zipped — not just the loaded page.
   *
   * One dated folder per cycle inside the archive, so it unpacks into the same
   * structure the screen shows rather than a flat pile of similarly-named PDFs.
   */
  const zipAll = async () => {
    setZipping('all')
    try {
      const all = await fetchAllClientLedgers({ request, from, to, sortBy })
      const files = []
      for (const folder of all) {
        for (const file of folder.files) {
          files.push({
            path: `${folder.receivedOn}/${file.name}`,
            bytes: await fetchLedgerFileBytes({ file }),
          })
        }
      }
      if (files.length) saveBlob(await buildZip(files), `${request.clientCode}-ledgers.zip`)
    } finally {
      setZipping(null)
    }
  }

  return (
    <Drawer
      title={request.clientName || request.clientCode}
      subtitle={`${request.clientCode} · ${total} ledger${total === 1 ? '' : 's'} collected`}
      onClose={onClose}
      toolbar={
        <>
        {/* Filter + sort. The date range narrows by when the statement was received. */}
        <div className="lfilter">
        {/* Label above each control, and the whole bar on one line. */}
        <div className="lfield">
          <span className="lfilter-lbl">Received between</span>
          <div className="lfield-row">
            <DateRangePicker
              from={draft.from}
              to={draft.to}
              closeSignal={closeSignal}
              onChange={setDraft}
            />
            {/*
              One action at a time, never both.
              Apply while there is an unapplied range to commit; Clear once the
              filter is actually doing something. Two buttons where only one can
              ever do anything is two things to read and one dead control.
            */}
            {canApply ? (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setFrom(draft.from)
                  setTo(draft.to)
                  setCloseSignal((n) => n + 1)
                }}
              >
                Apply
              </button>
            ) : canClear ? (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  setDraft({ from: '', to: '' })
                  setFrom('')
                  setTo('')
                  setCloseSignal((n) => n + 1)
                }}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <div className="lfield">
          <span className="lfilter-lbl">Sort</span>
          <div className="lfield-row">
            {/* One button showing the active order; clicking swaps it. With only
                two options the current state is the whole message — a second,
                greyed-out option just adds a thing to read past. */}
            <button
              type="button"
              className="btn btn-sm sortswap"
              onClick={() => setSortBy((v) => (v === 'newest' ? 'oldest' : 'newest'))}
              title="Click to switch the sort order"
            >
              <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                <path d="M7 4v16m0 0l-3.5-3.5M7 20l3.5-3.5M17 20V4m0 0l-3.5 3.5M17 4l3.5 3.5" />
              </svg>
              {sortBy === 'newest' ? 'Newest' : 'Oldest'}
            </button>
          </div>
        </div>
      </div>

      {/* Below the rule and above the folders, right-aligned: it acts on the whole
          set the filter has selected, so it belongs with the list rather than
          among the filter's own controls. */}
      {notice && <div className="gate pass" style={{ margin: '0 0 12px' }}><span className="gi">✓</span><span>{notice}</span></div>}

      {!notice && request.postReco?.needsPerson && (
        <div className="gate warn" style={{ margin: '0 0 12px' }}>
          <span className="gi">!</span>
          <span>
            <b>Needs a person.</b> We&apos;ve asked them {request.postReco.asks} times to correct their ledger and send it
            back, and it hasn&apos;t come, so the chase has paused. Call them, then upload their corrected statement here —
            that restarts the check. If they name someone else to ask, or promise a date, record it and the chase starts again.
          </span>
        </div>
      )}

      <div className="lallbar">
        {canUpload ? (
          <button type="button" className="btn btn-sm" onClick={() => setUploading(true)} title="File a statement that came in outside WhatsApp or email">
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" className="zipico">
              <path d="M12 21V9m0 0l-4 4m4-4l4 4M5 3h14" />
            </svg>
            Upload their statement
          </button>
        ) : (
          <span className="c-sub" style={{ marginTop: 0 }}>Not asked yet — start their period on Ledger Collector to file a statement.</span>
        )}
      {total > 0 && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={zipping !== null}
            onClick={zipAll}
            title="Every statement this client has sent (in this filter), as one zip"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" className="zipico">
              <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
            </svg>
            {zipping === 'all' ? 'Zipping…' : 'Download all their ledgers'}
          </button>
      )}
      </div>

      {uploading && (
        <UploadModal
          request={request}
          onClose={() => setUploading(false)}
          onDone={(out) => {
            setUploading(false)
            setNotice(
              out?.duplicate
                ? 'We already had this exact file — it’s linked to this cycle and marked received.'
                : 'Statement filed and marked received — the chase has stopped and it goes to reconciliation on the next pass.'
            )
            load(1, false)
            onChanged?.()
          }}
        />
      )}
        </>
      }
    >
      {folders.length === 0 && !openFolder && !loading && (
        <div className="note" style={{ marginTop: 4 }}>
          No ledgers in this range.
        </div>
      )}

      {openFolder && (
        <div className="fldrs" style={{ marginBottom: folders.length ? 8 : 0 }}>
          <div className="fldr pending">
            <div className="fldr-h">
              <span className="fldr-ico">
                <Icon name="ledger" size={15} />
              </span>
              <span className="fldr-meta">
                {/* "Not received" only once a month we were actually chasing
                    in has ended with nothing back — not merely because the
                    ledger's own period is in the past. A period that closed
                    before the first ask went out hasn't been ignored yet. */}
                <span className="fldr-title">
                  {openFolder.months?.some((m) => m.closed) ? 'Asked, not received' : 'Asked — nothing back yet'}
                </span>
                <span className="fldr-sub">
                  Asked for {dateText(openFolder.periodFrom)} till date
                  {openFolder.chaseStartedOn && ` · chasing since ${dateText(openFolder.chaseStartedOn)}`}
                </span>
              </span>
            </div>
            {/* The same open ask, broken out month by month — the chase
                keeps widening one request rather than opening a new one
                each month, so this is the only place that shows which
                individual months it's actually missed so far. */}
            {openFolder.months?.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '2px 0 8px 41px' }}>
                {openFolder.months.map((m) => (
                  <span key={m.label} className="c-sub">
                    {m.label} — {m.closed ? 'not received' : 'nothing back yet'}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {folders.length === 0 && loading && (
        <div className="fldrs">
          {Array.from({ length: 4 }, (_, i) => (
            <SkeletonBlock key={i} height={54} style={{ marginBottom: 8, borderRadius: 8 }} />
          ))}
        </div>
      )}

      <div className="fldrs">
        {folders.map((f) => {
          const isOpen = openId === f.id
          return (
            <div key={f.id} className={`fldr${isOpen ? ' open' : ''}`}>
              <button className="fldr-h" onClick={() => setOpenId(isOpen ? null : f.id)}>
                <span className="fldr-ico">
                  <Icon name="doc" size={15} />
                </span>
                <span className="fldr-meta">
                  <span className="fldr-title">Received {dateText(f.receivedOn)}</span>
                  {/* Which sides are present is answered by opening the folder —
                      the file list right below already says it, so repeating it
                      up here as "both ledgers"/"client's only" was one more
                      thing to read for a fact the folder itself already shows. */}
                  {/* What the statement actually covers — we ask "till date",
                      so this is where their books really stopped. */}
                  <span className="fldr-sub">
                    {f.coversFrom && f.coversTo
                      ? `Covers ${monthRange(f.coversFrom, f.coversTo)}`
                      : 'Months covered couldn’t be read from the file'}
                  </span>
                </span>
                {/* Their statement for this cycle — only when the file is
                    actually on record. `stopPropagation` so it downloads
                    rather than toggling the folder open. */}
                {f.files.length > 0 && (
                  <span
                    className="fldr-zip"
                    role="button"
                    tabIndex={0}
                    title="Download their ledger for this cycle"
                    onClick={(e) => {
                      e.stopPropagation()
                      downloadFolder(f)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        downloadFolder(f)
                      }
                    }}
                  >
                    {zipping === f.id ? (
                      <span className="wa-spin" />
                    ) : (
                      <svg viewBox="0 0 24 24" width="13" height="13">
                        <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
                      </svg>
                    )}
                    <span>{zipping === f.id ? 'Downloading…' : 'Download their ledger'}</span>
                  </span>
                )}
                <span className={`fldr-caret${isOpen ? ' up' : ''}`}>›</span>
              </button>

              {isOpen && (
                <div className="fldr-body">
                  {f.files.map((file) => (
                    <FileEntry key={file.key} file={file} />
                  ))}
                  {f.files.length === 0 && (
                    <div className="c-sub" style={{ padding: '6px 2px' }}>
                      Marked received, but their file isn’t on record, so there’s nothing to download.
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* The scroll sentinel. Rendered only while there is more to fetch, so the
          observer stops firing once everything is loaded. */}
      {hasMore && (
        <div ref={sentinelRef} className="lmore-sentinel">
          {loading ? 'Loading…' : `${total - folders.length} more`}
        </div>
      )}
    </Drawer>
  )
}

/**
 * A file in a folder — name, size, and a download.
 *
 * Same authenticated proxy the zip buttons already use, so a single file and a
 * folder full of them go through one code path: fetch the bytes, hand them to
 * the browser.
 */
function FileEntry({ file }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const download = async () => {
    setBusy(true)
    setErr(null)
    try {
      const bytes = await fetchLedgerFileBytes({ file })
      saveBlob(new Blob([bytes]), file.name)
    } catch (e) {
      setErr(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button className="fentry" onClick={download} disabled={busy} title={err || `Download ${file.name}`}>
      <span className="fentry-ico">
        <Icon name="doc" size={16} />
      </span>
      <span className="fentry-meta">
        <span className="fentry-name">{file.label}</span>
        <span className="fentry-sub">
          {file.name} · {file.sizeKB} KB
        </span>
      </span>
      <span className="fentry-dl">
        {busy ? (
          '…'
        ) : (
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
          </svg>
        )}
      </span>
    </button>
  )
}
